/**
 * The home's time zone in the driver app (item 4, 2026-10-02). Warren:
 * "Timezone: set the home timezone from the device during Set up My Home,
 * editable after."
 *
 * Set up My Home sends the phone's zone. When the phone keeps none of the
 * clocks the address allows (one zone for Ontario, California or Arizona;
 * either half of a split state like Texas), the form asks the host which
 * one to use instead of picking silently: someone setting up while
 * travelling would otherwise stamp the wrong clock on every guest window.
 * Home Settings then lists the country's zones (HOME_TIME_ZONES) for later
 * edits.
 *
 * Pure apart from reading the runtime's zone; the rules live in
 * @evi/shared (regionTimeZone.ts) so the Api applies the same ones.
 */
import {
  HOME_TIME_ZONES, canonicalTimeZone, homeRegionZones, isGeographicHomeZone, sameCivilClock,
  timeZoneCityLabel, timeZoneShortLabel, type HomeCountry,
} from '@evi/shared';

/** The phone's IANA zone, or null when the runtime reports none. */
export function deviceTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * "Toronto (Eastern Time)", the generic name in the reader's language.
 * Falls back to the short name ("EDT"), then the city alone, never an
 * invented abbreviation.
 */
export function homeZoneLabel(zone: string, locale?: string): string {
  const city = timeZoneCityLabel(zone);
  let generic: string | null = null;
  try {
    generic = new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: 'longGeneric' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value ?? null;
  } catch {
    generic = timeZoneShortLabel(zone, locale);
  }
  return generic ? `${city} (${generic})` : city;
}

export type SetupZonePlan =
  /** Use the phone's zone: it keeps one of the address's clocks, or the
   *  region is not known yet. */
  | { kind: 'device'; zone: string }
  /** The phone's zone is unusable (UTC, none); the region has one zone. */
  | { kind: 'address'; zone: string }
  /** The phone keeps none of the address's clocks: the host chooses
   *  between the address's zones (one, or both halves of a split state)
   *  and the phone's. */
  | { kind: 'ask'; device: string; address: string[] }
  /** Nothing to go on: the home starts with no zone. */
  | { kind: 'none' };

/**
 * What Set up My Home does about the zone, from the phone's zone and the
 * region code typed so far (null until it resolves).
 */
export function planSetupTimeZone(
  deviceZone: string | null,
  regionCode: string | null,
  at: Date = new Date(),
): SetupZonePlan {
  const device = isGeographicHomeZone(deviceZone) ? deviceZone : null;
  const address = regionCode ? homeRegionZones(regionCode) : [];
  if (device && address.length > 0 && !address.some((zone) => sameCivilClock(device, zone, at))) {
    return { kind: 'ask', device, address };
  }
  if (device) return { kind: 'device', zone: device };
  if (address.length === 1) return { kind: 'address', zone: address[0]! };
  return { kind: 'none' };
}

/** What the app based the zone on, for the Api's audit row: the phone,
 *  the address (the phone had none), or the host's answer. */
export type SetupZoneSource = 'device' | 'address' | 'host';

/**
 * The zone POST /me/home carries, and what it was based on. Undefined
 * leaves it to the server (its fallback is the same address rule); null
 * means the host still has to answer the question, so the form is not
 * ready. `choice` is the zone the host picked, one of the plan's.
 */
export function setupTimeZoneForBody(
  plan: SetupZonePlan,
  choice: string | null,
): { timezone: string; source: SetupZoneSource } | undefined | null {
  switch (plan.kind) {
    case 'device':
    case 'address':
      return { timezone: plan.zone, source: plan.kind };
    case 'ask':
      return choice !== null && (choice === plan.device || plan.address.includes(choice))
        ? { timezone: choice, source: 'host' }
        : null;
    default:
      return undefined;
  }
}

/**
 * The Home Settings choices: the home country's zones (both countries'
 * when it has none), so the select can always show what is saved. A
 * stored zone that is another spelling of a listed one (browsers report
 * America/Indianapolis for America/Indiana/Indianapolis) takes that
 * entry's place, so the city is not listed twice; any other stored zone
 * outside the list goes first.
 */
export function homeZoneOptions(country: HomeCountry | null, stored: string | null): string[] {
  const base = country ? [...HOME_TIME_ZONES[country]] : [...HOME_TIME_ZONES.US, ...HOME_TIME_ZONES.CA];
  if (!stored || base.includes(stored)) return base;
  const canonical = canonicalTimeZone(stored);
  const same = base.findIndex((zone) => canonicalTimeZone(zone) === canonical);
  if (same === -1) return [stored, ...base];
  base[same] = stored;
  return base;
}
